import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getProjects } from "../../api/projectsApi";
import { queryMediaAssets, searchMediaAssets } from "../../api/deploymentsApi";
import {
  BulkReviewActionResponse,
  DeploymentResponseDto,
  ProjectResponseDto,
  MediaAssetListDto,
  MediaAssetSearchDto,
  MediaQueryCounts,
} from "../common/ObjectTypes";
import tailwindClassnames from "../common/TailwindClassnames";
import { BeatLoader } from "react-spinners";
import Pagination from "../common/Pagination";
import { DownloadIcon, FilterIcon } from "../common/ProjectIcons";
import ExportPanel from "./ExportPanel";
import ReviewPageFilter, { ReviewPageFilterHandle } from "./ReviewPageFilter";
import FullScreenImageReview from "./FullScreenImageReview";
import { Image } from "../common/ObjectTypes";
import { useNavigate, useParams } from "react-router-dom";
import BoundingBoxOverlay from "./BoundingBoxOverlay";
import BulkActionsBar from "./BulkActionsBar";
import ConfirmationModal from "../common/ConfirmationModal";
import { isTopLayerOpen } from "../common/topLayer";
import ObservationEditor from "./ObservationEditor";
import PointToSpeciesModal from "./PointToSpeciesModal";
import { EditFields } from "./reviewActions";
import {
  bulkAcceptSuggestions,
  bulkCreateObservation,
  bulkMarkBlank,
  bulkPointToSpecies,
} from "../../api/reviewActionsApi";
import {
  SkipReasonMessage,
  bulkErrorMessageKey,
  describeBulkResult,
  pointToSpeciesSkip,
} from "./bulkReviewActions";
import { composeDetailBoxes, composeFrameLabels } from "./mediaDetailComposer";
import {
  MediaSortKey,
  MediaDetailNavigationContext,
  ReviewStatusFilter,
} from "../common/QueryTypes";
import ReviewQueueHeader, {
  buildFilterChips,
  FilterDimension,
} from "./ReviewQueueHeader";
import ReviewQueueStats from "./ReviewQueueStats";
import {
  buildMediaQuery,
  emptyFilterState,
  ReviewFilterState,
  ReviewSortState,
  validateFilters,
} from "./mediaQueryBuilder";

type GridDensity = "S" | "M" | "L";
const DENSITY_MIN_WIDTH: Record<GridDensity, number> = {
  S: 140,
  M: 200,
  L: 300,
};

// `<input>` types that do not accept typed text, so a keystroke landing on one
// is a shortcut rather than something the reviewer is writing (AKL-753).
const NON_TEXT_INPUT_TYPES = new Set([
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "image",
  "file",
  "range",
  "color",
]);

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const PAGE_SIZE_STORAGE_KEY = "cachedReviewPageSize";

const ManageReviewPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Images variables
  const [paginatedImages, setPaginatedImages] = useState<Image[]>([]);
  // AKL-753: keyed by `Image.id`, not by object identity. `refreshCurrentView`
  // drops the page cache and re-queries after every review edit, so an
  // Image[]-and-`includes` selection silently stopped matching the rendered
  // tiles the moment anything refetched. Same key `optimisticallyReviewedImageIds`
  // uses in the full-screen surface.
  const [selectedImageIds, setSelectedImageIds] = useState<Set<string>>(
    new Set(),
  );
  // AKL-753: bulk-select mode. While on, every tile shows a checkbox and a plain
  // click selects the photo instead of opening it — so selecting is deliberate
  // and reversible rather than a modifier key nobody discovers. Toggled by the
  // Bulk Select button or `S`; Escape leaves the mode and drops the selection.
  const [bulkMode, setBulkMode] = useState(false);
  // The Bulk Select button, so leaving the mode can put focus somewhere
  // deliberate instead of letting it fall to `<body>` when a focused tile
  // checkbox unmounts.
  const bulkToggleRef = useRef<HTMLButtonElement>(null);
  // AKL-753 bulk-action state. `bulkPending` names the confirmation currently
  // open; the add-observation flow is two-stage, so it also carries the fields
  // and species label the editor produced.
  const [bulkPending, setBulkPending] = useState<
    | { kind: "mark-blank" }
    | { kind: "accept" }
    | { kind: "add-observation"; fields: EditFields; label: string | null }
    | null
  >(null);
  const [bulkEditorOpen, setBulkEditorOpen] = useState(false);
  // AKL-811 point-to-one-species. A boolean of its own rather than a fourth arm
  // of `bulkPending`, because this flow is single-stage: the modal *is* the
  // confirmation, so there is no editor result to carry into a second dialog.
  const [pointToSpeciesOpen, setPointToSpeciesOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  // The "Also set photos to Reviewed" opt-in, shared by the accept and
  // add-observation confirmations (only one is ever open, so one piece of state
  // serves both). Checked by default (Margaux, 2026-08-28) — both actions
  // usually do finish the photos, so the default matches the common case, and
  // each flow resets it on open so one batch's answer never carries into the
  // next. Mark as blank has no such choice: blanking a photo *is* finishing it.
  const [bulkSetReviewed, setBulkSetReviewed] = useState(true);
  // Loading variables
  const [loading, setLoading] = useState(false);
  // Set while fetching additional images to append for a page-size increase;
  // kept separate from `loading` so the already-loaded grid stays visible
  // instead of being hidden (the grid only renders when `!loading`).
  const [loadingMore, setLoadingMore] = useState(false);
  // Set while refreshCurrentView re-syncs counts/thumbnails after the
  // full-screen viewer closes. Its own flag, not loadingMore — the two fetches
  // are independent and can overlap (e.g. a page-size grow still in flight
  // when a photo is opened and closed), and sharing one boolean would let
  // whichever resolves first clear it while the other is still running.
  const [refreshing, setRefreshing] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(true);
  // Pagination state for Azure blob fetching
  const [totalImageCount, setTotalImageCount] = useState(0);
  const [imageCache, setImageCache] = useState<Map<number, Image[]>>(new Map());
  const [selectedProject, setSelectedProject] =
    useState<ProjectResponseDto | null>(null);
  // Explicitly-picked deployments from the filter panel (Location/Sensor no
  // longer factor into this — they're independent `filterState` filters now).
  // Exactly one entry drives the deep-linkable single-deployment URL and the
  // full-screen image-search-by-filename feature; any other count behaves as
  // a project-scoped query.
  const [selectedDeployments, setSelectedDeployments] = useState<
    DeploymentResponseDto[]
  >([]);
  const singleSelectedDeployment =
    selectedDeployments.length === 1 ? selectedDeployments[0] : null;
  const [projects, setProjects] = useState<ProjectResponseDto[]>([]);
  const reviewFilterRef = useRef<ReviewPageFilterHandle>(null);
  const [filteredDeployments, setFilteredDeployments] = useState<
    DeploymentResponseDto[]
  >([]);
  // Pagination variables
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [pageSizeRestored, setPageSizeRestored] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [isWholeProject, setIsWholeProject] = useState(false);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  // View image in full screen
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  // Select multiple images
  const [shiftKeyDown, setShiftKeyDown] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  // AKL-680 media query state: advanced filters, sort, density, and counts.
  const [filterState, setFilterState] =
    useState<ReviewFilterState>(emptyFilterState());
  const [sortState, setSortState] = useState<ReviewSortState>({
    key: "capture_time_asc",
    seed: null,
  });
  const [gridDensity, setGridDensity] = useState<GridDensity>("M");
  const [counts, setCounts] = useState<MediaQueryCounts | null>(null);
  // The Review Status filter that was in effect when `counts` was fetched. Kept
  // apart from the live `filterState` because the filter dialog writes to that
  // on every control change, before Apply — the stat tiles must not move until
  // the counts for the new filter have actually arrived.
  const [countsReviewStatus, setCountsReviewStatus] =
    useState<ReviewStatusFilter | null>(null);
  const [showExportPanel, setShowExportPanel] = useState(false);
  // Refs mirror the latest filter/sort so the fetch helpers read fresh values
  // without being re-created on every keystroke (avoids stale closures).
  const filterStateRef = useRef(filterState);
  const sortStateRef = useRef(sortState);
  useEffect(() => {
    filterStateRef.current = filterState;
  }, [filterState]);
  useEffect(() => {
    sortStateRef.current = sortState;
  }, [sortState]);
  /**
   * `filterState` with the page's resolved deployment scope folded in — the shape the
   * feed query actually sends.
   *
   * `filterState.deploymentIds` is only the filter panel's own selection; the grid is
   * additionally scoped by `isWholeProject` / `filteredDeployments`, which is how
   * choosing a location or sensor narrows the feed. Every `buildMediaQuery` call for the
   * feed spreads this same override inline, so anything that skips it (the export did)
   * silently addresses a different working set — a deployment-scoped grid would have
   * exported the whole project.
   */
  const scopedFilterState: ReviewFilterState = useMemo(
    () => ({
      ...filterState,
      deploymentIds: isWholeProject
        ? []
        : filteredDeployments.map((d) => d.deployment_id),
    }),
    [filterState, isWholeProject, filteredDeployments],
  );
  // Go to image full screen
  const { imageName } = useParams<{ imageName: string }>();

  /**
   * Load available projects for user.
   */
  useEffect(() => {
    const loadProjects = async () => {
      setLoadingProjects(true);
      try {
        const temp = await getProjects({
          skip: 0,
          all_projects: true,
          permission: "VIEW_DEPLOYMENTS_LOCATIONS",
        });
        setProjects(temp.projects);
      } catch (err) {
        console.error("Error loading projects:", err);
      } finally {
        setLoadingProjects(false);
      }
    };
    loadProjects();
  }, []);

  /**
   * Restore the reviewer's preferred page size from localStorage on mount.
   * `pageSizeRestored` flips true once this has run (whether or not a cached
   * value existed) so the deep-link image search effect below can wait for it
   * — otherwise that effect could fire once with the default 25 and again
   * moments later once the cached size lands, doubling the request.
   */
  useEffect(() => {
    const cached = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
    if (PAGE_SIZE_OPTIONS.includes(cached)) {
      setPageSize(cached);
    }
    setPageSizeRestored(true);
  }, []);

  /**
   * Load next page of data preemptively.
   */
  useEffect(() => {
    if (
      selectedProject &&
      (isWholeProject || filteredDeployments.length > 0) &&
      paginatedImages.length > 0 &&
      currentPage < totalPages &&
      !loading &&
      !loadingMore &&
      !refreshing
    ) {
      let cancelled = false;

      const fetchNextPage = async () => {
        try {
          const images = await getImagesForPage(currentPage + 1);
          if (cancelled) return;
          if (!images) {
            console.error("No images returned for next page.");
            return;
          }

          setImageCache((prev) => {
            const newCache = new Map(prev);
            newCache.set(currentPage + 1, images);
            return newCache;
          });
        } catch (error) {
          if (!cancelled) {
            console.error("Error fetching page images:", error);
          }
        }
      };
      // Only fetch next page if not already in cache
      if (!imageCache.has(currentPage + 1)) {
        fetchNextPage();
      }

      return () => {
        cancelled = true;
      };
    }
  }, [
    selectedProject,
    filteredDeployments,
    isWholeProject,
    paginatedImages,
    currentPage,
    totalPages,
    loading,
    loadingMore,
    refreshing,
    imageCache,
  ]);

  /**
   * Check if imageCache is updated for current page while waiting on return from API.
   */
  useEffect(() => {
    if (
      paginatedImages.length === 0 &&
      imageCache.has(currentPage) &&
      loading
    ) {
      const cachedImages = imageCache.get(currentPage);
      if (cachedImages) {
        setPaginatedImages(cachedImages);
        setLoading(false);
      }
    }
  }, [imageCache, currentPage, paginatedImages, loading]);

  /**
   * Loads the image from the URL params if applicable.
   */
  useEffect(() => {
    if (
      selectedIndex == null && // Selected index not already set, but there is an image in the URL
      imageName &&
      selectedProject &&
      singleSelectedDeployment &&
      pageSizeRestored // wait for the cached page size so this doesn't fire once with the default and again once restored
    ) {
      const searchForImageInDeployment = async () => {
        try {
          const searchResults: MediaAssetSearchDto = await searchMediaAssets(
            selectedProject.project_id,
            [singleSelectedDeployment.deployment_id],
            imageName,
            pageSize,
          );
          if (searchResults) {
            const images = getImagesFromMediaAssetsDto(
              searchResults,
              [singleSelectedDeployment],
              selectedProject.guid,
            );
            setTotalImageCount(searchResults.total);
            setTotalPages(Math.ceil(searchResults.total / pageSize));
            setPaginatedImages(images);
            const pageNumber = searchResults.page;
            setCurrentPage(pageNumber); // currentPage is 1-based, searchResults.page is also 1-based
            if (
              searchResults.image_found === true &&
              searchResults.image_index !== null
            ) {
              setSelectedIndex(searchResults.image_index);
            } else {
              // remove image name from URL
              toast.error(
                t("Image {{filename}} not found in deployment.", {
                  filename: imageName,
                }),
              );
              navigate(
                `/review/${selectedProject.guid}/${singleSelectedDeployment.guid}`,
                {
                  replace: true,
                },
              );
            }
            // Reset the cache to just this page since search results represent a new data context
            setImageCache(new Map([[pageNumber, images]]));
          } else {
            toast.error(
              t("Image {{filename}} not found in deployment.", {
                filename: imageName,
              }),
            );
            navigate(
              `/review/${selectedProject.guid}/${singleSelectedDeployment.guid}`,
              {
                replace: true,
              },
            );
          }
        } catch (error) {
          toast.error(t("Error searching for image in deployment."));
          console.error("Error searching for image in deployment:", error);
        }
      };
      searchForImageInDeployment();
    }
  }, [
    selectedProject,
    singleSelectedDeployment,
    imageName,
    selectedIndex,
    pageSize,
    pageSizeRestored,
  ]);

  /**
   * Fetches images for a given page, using cache if available.
   * @param page the page number to fetch images for
   * @returns the images for the given page
   */
  const getImagesForPage = async (
    page: number,
  ): Promise<Image[] | undefined> => {
    // Load images for the selected page from cache if available
    const cachedImages = imageCache.get(page);
    if (cachedImages) {
      return cachedImages;
    }
    if (!selectedProject || !selectedProject.guid) {
      console.error("No selected project.");
      return;
    }
    if (!isWholeProject && filteredDeployments.length === 0) {
      console.error("No deployments to load images from.");
      return;
    }
    if (!validateFilters(filterStateRef.current).valid) {
      console.error("Cannot fetch: invalid filter values.");
      return;
    }
    try {
      const query = buildMediaQuery(
        selectedProject.project_id,
        {
          ...filterStateRef.current,
          deploymentIds: isWholeProject
            ? []
            : filteredDeployments.map((d) => d.deployment_id),
        },
        sortStateRef.current,
        (page - 1) * pageSize,
        pageSize,
      );
      const result = await queryMediaAssets(query);
      return getImagesFromMediaAssetsDto(
        result,
        filteredDeployments,
        selectedProject.guid,
      );
    } catch (error) {
      console.error("Error fetching page images:", error);
    }
  };

  /**
   * Handles clicking on a page number in the pagination element.
   * @param page the page number selected
   */
  const onPageClick = async (page: number): Promise<Image[] | undefined> => {
    setLoading(true);
    // AKL-753: a selection never outlives the view that made it. With no
    // select-all, that is what guarantees a bulk action can only ever touch
    // photos the reviewer actually had on screen — so no "N hidden by the
    // current filter" warning is needed, unlike the AI Mappings table.
    clearSelection();
    setCurrentPage(page);
    setPaginatedImages([]); // Set empty while loading so old images aren't shown while new ones load
    // Load images for the selected page from cache if available
    try {
      const images = await getImagesForPage(page);
      if (images) {
        setPaginatedImages(images);
        // Update cache
        if (!imageCache.has(page)) {
          const newCache = new Map(imageCache);
          newCache.set(page, images);
          setImageCache(newCache);
        }
      }
      // Return the loaded page so callers (e.g. cross-page stepping) can react
      // only when the fetch actually produced images.
      return images ?? undefined;
    } catch (error) {
      console.error("Error loading images for page:", error);
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles the reviewer changing the page size, updating the grid in place
   * rather than reloading from scratch:
   * - On page 1, shrinking just trims the already-loaded images (no request).
   * - On page 1, growing fetches only the additional images and appends them
   *   below the ones already on screen.
   * - Anywhere else, the page's contents change entirely, so it falls back to
   *   a full refetch (passing the new size explicitly, since it wouldn't take
   *   effect in the `pageSize` state until the next render).
   * The new size is only persisted to state/localStorage once an update path
   * actually commits to applying it — if filters are currently invalid, the
   * fetch can't run, and persisting anyway would leave the selector out of
   * sync with what's still on screen.
   * @param size the newly selected page size
   */
  const handlePageSizeChange = async (size: number) => {
    const persist = () => {
      setPageSize(size);
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(size));
    };

    if (currentPage === 1 && selectedProject && paginatedImages.length > 0) {
      if (
        size <= paginatedImages.length ||
        paginatedImages.length >= totalImageCount
      ) {
        // Either shrinking, or every matching image is already loaded (e.g. a
        // small deployment) — no request needed either way.
        persist();
        const trimmed = paginatedImages.slice(0, size);
        setPaginatedImages(trimmed);
        setImageCache(new Map([[1, trimmed]]));
        setTotalPages(Math.ceil(totalImageCount / size));
        return;
      }
      if (!validateFilters(filterStateRef.current).valid) {
        return;
      }
      persist();
      setLoadingMore(true);
      try {
        const query = buildMediaQuery(
          selectedProject.project_id,
          {
            ...filterStateRef.current,
            deploymentIds: isWholeProject
              ? []
              : filteredDeployments.map((d) => d.deployment_id),
          },
          sortStateRef.current,
          paginatedImages.length,
          size - paginatedImages.length,
        );
        const result = await queryMediaAssets(query);
        const newImages = getImagesFromMediaAssetsDto(
          result,
          filteredDeployments,
          selectedProject.guid,
        );
        const combined = [...paginatedImages, ...newImages];
        setPaginatedImages(combined);
        setImageCache(new Map([[1, combined]]));
        setTotalImageCount(result.total);
        setTotalPages(Math.ceil(result.total / size));
      } catch (error) {
        console.error("Error loading additional images for page size:", error);
      } finally {
        setLoadingMore(false);
      }
      return;
    }

    if (selectedProject) {
      if (!validateFilters(filterStateRef.current).valid) {
        return;
      }
      persist();
      applyFilters(selectedProject, filteredDeployments, isWholeProject, size);
    } else {
      // No active grid to desync from — always safe to persist the preference.
      persist();
    }
  };

  /**
   * Converts MediaAssetListDto to Image[]. Predictions/suggestions/observations
   * on each asset (AKL-731: the same unified DTO family the detail endpoint
   * returns) are composed into a render-ready box list via `composeDetailBoxes`
   * — the same compose function full-screen review uses, so the grid and film
   * strip render identical boxes/labels for the same photo, with no client-side
   * inclusion or label-fallback logic of their own. Whole-media classification
   * badges are unaffected by that unification and still come from
   * `composeFrameLabels`. Per-asset review status (AKL-696) is carried through
   * so the grid + filmstrip can flag reviewed assets.
   * @param dto the MediaAssetListDto to convert
   * @param currentDeployments the deployments to use for name/guid lookup (pass explicitly to avoid stale closure)
   * @returns the converted Image[]
   */
  const getImagesFromMediaAssetsDto = (
    dto: MediaAssetListDto,
    currentDeployments: DeploymentResponseDto[],
    projectGuid: string,
  ): Image[] => {
    try {
      if (!dto || !dto.media_assets) {
        console.error("Invalid MediaAssetListDto: missing media_assets data");
        return [];
      }

      const deploymentById = new Map(
        currentDeployments.map((d) => [d.deployment_id, d]),
      );

      const images = dto.media_assets
        .map((asset) => {
          if (!asset) {
            console.warn("Skipping null or undefined asset");
            return null;
          }

          const dep = deploymentById.get(asset.deployment_id);
          const boxes = composeDetailBoxes({
            predictions: asset.predictions ?? [],
            suggestions: asset.suggestions ?? [],
            observations: asset.observations ?? [],
          });
          const frameLabels = composeFrameLabels(asset.predictions);

          return {
            id: String(asset.media_asset_id),
            url: asset.file_uri,
            name: asset.file_name,
            projectGuid: projectGuid,
            deploymentGuid: dep?.guid ?? "",
            deployment: dep?.deployment_name ?? "",
            location: dep?.location_name ?? "",
            sensor: dep?.sensor_name ?? "",
            captureTime: asset.capture_time_actual,
            boxes,
            frameLabels,
            review_status: asset.review_status,
            last_reviewed_at: asset.last_reviewed_at,
          } as Image;
        })
        .filter((image): image is Image => image !== null);

      return images;
    } catch (error) {
      console.error("Error converting MediaAssetListDto to Images:", error);
      return [];
    }
  };

  /**
   * Fetches the images based on the selected project and deployments.
   * @param project the selected project
   * @param deployments deployments to use for the query and metadata lookup
   * @param isWholeProject when true, fetches by project_id (no deployment filter); when false, fetches by deployment_ids
   * @param sizeOverride page size to use for this fetch instead of the `pageSize` state; needed by
   * the page-size selector so the new size takes effect immediately instead of on the next render.
   * When omitted, falls back to the persisted localStorage value rather than the `pageSize` state
   * directly, since this can be called (e.g. from the cached-filter restore flow) before the
   * page-size-restore effect has had a chance to flush `pageSize` to its cached value.
   */
  const applyFilters = async (
    project: ProjectResponseDto | null,
    deployments: DeploymentResponseDto[],
    isWholeProject: boolean,
    sizeOverride?: number,
  ) => {
    const cachedPageSize = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
    const effectivePageSize =
      sizeOverride ??
      (PAGE_SIZE_OPTIONS.includes(cachedPageSize) ? cachedPageSize : pageSize);
    // Guard BEFORE mutating any view state: on invalid filter values (e.g.
    // confidence bounds) keep the current results and the open builder so the
    // user can correct them, rather than wiping the feed for a request that
    // would never run. (Skip the guard for the project===null reset path.)
    if (project !== null && !validateFilters(filterStateRef.current).valid) {
      return;
    }
    setLoading(true);
    setShowFilterPopup(false);
    // Same rule as onPageClick: changing the filter starts the selection over.
    clearSelection();
    setFilteredDeployments(deployments);
    setIsWholeProject(isWholeProject);
    setPaginatedImages([]);
    setImageCache(new Map());
    setCurrentPage(1);
    setTotalImageCount(0);
    setTotalPages(0);
    // If no project selected, reset to initial state
    if (project === null) {
      setFiltersApplied(false);
      navigate(`/review`, { replace: true });
      setLoading(false);
      return;
    }

    // No deployments matched the filter
    if (!isWholeProject && deployments.length === 0) {
      setCounts(null);
      setLoading(false);
      setFiltersApplied(true);
      return;
    }
    try {
      // Snapshot the filters this request is built from. The builder can be
      // reopened and edited while the request is in flight, so the review
      // status committed beside the response must be the one that produced
      // it, not whatever the ref says once the response lands.
      const requestedFilters = filterStateRef.current;
      const query = buildMediaQuery(
        project.project_id,
        {
          ...requestedFilters,
          deploymentIds: isWholeProject
            ? []
            : deployments.map((d) => d.deployment_id),
        },
        sortStateRef.current,
        0,
        effectivePageSize,
      );
      const result = await queryMediaAssets(query);
      // Set total image count and total pages
      setTotalImageCount(result.total);
      setTotalPages(Math.ceil(result.total / effectivePageSize));
      setCounts(result.counts);
      setCountsReviewStatus(requestedFilters.reviewStatus);
      setFiltersApplied(true);
      // Then fetch images for the first page
      const images = getImagesFromMediaAssetsDto(
        result,
        deployments,
        project.guid,
      );
      setPaginatedImages(images);
      // Cache the first page as a new map when filters change
      const newCache = new Map();
      newCache.set(1, images);
      setImageCache(newCache);
    } catch (error) {
      console.error("Error applying filters:", error);
      setTotalImageCount(0);
      setTotalPages(0);
      setCounts(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Re-runs the query for the current scope (project + deployments) using the
   * latest filter/sort state. Called when advanced filters, sort, or the random
   * seed change. Clears the page cache and resets to page 1 (in applyFilters).
   */
  const reapplyCurrentFilters = () => {
    if (!selectedProject) return;
    applyFilters(selectedProject, filteredDeployments, isWholeProject);
  };

  /**
   * Merge a partial change into the advanced filter state (edited from the
   * builder). Does not fetch — the builder applies changes explicitly.
   * @param partial the filter fields to update
   */
  const updateFilterState = (partial: Partial<ReviewFilterState>) => {
    // Sync the ref synchronously (not just via the post-render effect) so that
    // applyFilters/getImagesForPage, which read filterStateRef.current, always
    // see the latest values even if invoked in the same tick as an edit.
    const next = { ...filterStateRef.current, ...partial };
    filterStateRef.current = next;
    setFilterState(next);
  };

  /**
   * Clears a single filter dimension from the active-filter chip bar and
   * re-runs the query. Deployment is still a scope filter — delegated to
   * ReviewPageFilter via ref so the deployments list is refetched with the
   * remaining scope before re-applying. Location and Sensor are plain
   * `ReviewFilterState` fields now (like Species), so they clear the same way.
   * @param dimension the filter dimension to clear
   */
  const handleClearFilter = (dimension: FilterDimension) => {
    if (dimension === "deployment") {
      reviewFilterRef.current?.clearScopeFilter("deployment");
      return;
    }
    const next = { ...filterStateRef.current };
    switch (dimension) {
      case "location":
        next.locationIds = [];
        break;
      case "sensor":
        next.sensorIds = [];
        break;
      case "reviewStatus":
        next.reviewStatus = null;
        break;
      case "date":
        next.captureTimeFrom = null;
        next.captureTimeTo = null;
        break;
      case "confidence":
        next.confidenceMin = null;
        next.confidenceMax = null;
        break;
      case "species":
        next.predictedTaxonIds = [];
        break;
      case "observationType":
        next.observationType = null;
        break;
      case "inferenceState":
        next.inferenceState = null;
        break;
    }
    filterStateRef.current = next;
    setFilterState(next);
    reapplyCurrentFilters();
  };

  /**
   * Changes the sort key (generating a fresh random seed when switching to
   * random) and re-runs the query.
   * @param key the new sort key
   */
  const handleSortChange = (key: MediaSortKey) => {
    // No-op when re-selecting the already-active non-random sort (avoids an
    // unnecessary cache clear + refetch). Random always reshuffles (new seed).
    if (key === sortStateRef.current.key && key !== "random") {
      return;
    }
    const next: ReviewSortState = {
      key,
      seed: key === "random" ? Math.floor(Math.random() * 1_000_000_000) : null,
    };
    sortStateRef.current = next;
    setSortState(next);
    reapplyCurrentFilters();
  };

  /**
   * Toggles the capture-time sort direction (asc/desc) and re-runs the query.
   */
  const handleToggleCaptureDirection = () => {
    const next: ReviewSortState = {
      key:
        sortStateRef.current.key === "capture_time_desc"
          ? "capture_time_asc"
          : "capture_time_desc",
      seed: null,
    };
    sortStateRef.current = next;
    setSortState(next);
    reapplyCurrentFilters();
  };

  /**
   * Generates a new random seed (reshuffle) and re-runs the query.
   */
  const handleReshuffle = () => {
    const next: ReviewSortState = {
      key: "random",
      seed: Math.floor(Math.random() * 1_000_000_000),
    };
    sortStateRef.current = next;
    setSortState(next);
    reapplyCurrentFilters();
  };

  /**
   * Steps the full-screen view to the previous (-1) or next (+1) photo, walking
   * the in-memory feed and crossing page boundaries by fetching the adjacent
   * page. Shared by the arrow keys and the on-screen prev/next controls so both
   * follow the exact feed ordering.
   * @param direction -1 for previous, +1 for next
   */
  /**
   * Opens the photo at the given page-relative index in full screen, always
   * navigating (never shift-selecting). Used by arrow/prev/next stepping and the
   * review-queue strip, so holding Shift can't turn navigation into multi-select.
   * @param index the index within the current page
   */
  const openImageAt = (index: number) => {
    setSelectedIndex(index);
    clearSelection();
  };

  const stepImage = (direction: -1 | 1) => {
    if (selectedIndex === null) return;
    if (direction === 1) {
      if (selectedIndex < paginatedImages.length - 1) {
        openImageAt(selectedIndex + 1);
      } else if (currentPage < totalPages) {
        // Only open the first photo of the next page once it actually loaded,
        // so a failed fetch (empty page) can't select an out-of-range index.
        onPageClick(currentPage + 1).then((imgs) => {
          if (imgs && imgs.length > 0) openImageAt(0);
        });
      }
    } else {
      if (selectedIndex > 0) {
        openImageAt(selectedIndex - 1);
      } else if (currentPage > 1) {
        // Open the last photo of the previous page, indexing by the loaded
        // page's real length (not pageSize) and only when it loaded.
        onPageClick(currentPage - 1).then((imgs) => {
          if (imgs && imgs.length > 0) openImageAt(imgs.length - 1);
        });
      }
    }
  };

  /**
   * Handles grid shift-select keys. Arrow stepping / Escape-close for the open
   * full-screen surface are owned by `FullScreenImageReview` itself (AKL-682),
   * which routes them through the unsaved-changes guard — so they are NOT bound
   * here to avoid double-stepping while the surface is open.
   * @param e the key event
   */
  const handleKeyDown = (e: KeyboardEvent) => {
    // A dialog layered above the page owns the keyboard while it is open (AKL-798). This is
    // the fourth `window` keydown handler over the review surface and it needs the same guard
    // as the other three: its `modalOpen` gate lists only this page's own dialogs, so with the
    // Report a problem dialog open over the GRID — theater closed, no review modal up — focus
    // on the dialog's drop-zone button is neither typing nor a listed modal, and `s` still ran
    // `toggleBulkMode()` behind it, entering or leaving bulk mode and dropping the reviewer's
    // tile selection. Above the Shift branch as well, so the grid does not pick up shift state
    // from a range-select the reviewer is making inside the dialog.
    if (isTopLayerOpen()) return;
    if (e.key === "Shift") {
      setShiftKeyDown(true);
      return;
    }

    // Everything below is a grid shortcut, so it must not fire while the
    // full-screen surface owns the keyboard, while a modal is up, or while the
    // reviewer is typing in the filter panel.
    const target = e.target as HTMLElement | null;
    const tag = target?.tagName;
    // Only inputs that actually swallow character keys count as "typing". A
    // checkbox is an <input> but consumes nothing except Space, so treating it
    // as typing left keyboard users stuck: once focus was on a tile checkbox,
    // neither Escape nor `S` could leave bulk mode. `<select>` stays in the
    // list — letter keys drive its type-ahead.
    const typing =
      (tag === "INPUT" &&
        !NON_TEXT_INPUT_TYPES.has((target as HTMLInputElement).type)) ||
      tag === "TEXTAREA" ||
      tag === "SELECT" ||
      target?.isContentEditable;
    // `pointToSpeciesOpen` is in here for a reason the checkbox exemption above
    // makes sharp (AKL-811): that modal has a checkbox which starts focusable
    // and ticked, and a focused checkbox deliberately does NOT count as typing.
    // Without this flag, `S` pressed with it focused would toggle bulk mode and
    // clear the selection underneath the open modal — which would then read
    // "Record 0 selected photos" and, on confirm, hit `runBulkAction`'s
    // empty-selection bail and do nothing at all. Escape would do the same while
    // looking like it merely closed a dialog. (Escape *inside* the modal is the
    // modal's own capture-phase listener; it stops propagation when it closes
    // the modal and stands aside when its species menu is open, which is why
    // this gate — not that listener — is what protects the selection.)
    const modalOpen =
      bulkPending !== null ||
      bulkEditorOpen ||
      pointToSpeciesOpen ||
      showFilterPopup;
    if (selectedIndex !== null || typing || modalOpen) return;

    if (e.key === "Escape") {
      // Leaves the mode and drops the selection in one press. Gated behind
      // `modalOpen` with everything else: Escape used to run regardless, so it
      // would wipe the selection out from under an open confirmation (leaving
      // the dialog reading "0 photos"), or throw the selection away just
      // because the reviewer backed out of the species picker.
      //
      // The observation editor keeps its own capture-phase Escape, so cancelling
      // it still works. A confirmation answers Escape itself since AKL-883
      // (`ModalDialog` closes it and stops the key propagating), so this gate
      // only has to keep the page's Escape out of the way while one is open.
      if (bulkMode) exitBulkMode();
      else clearSelection();
      return;
    }
    if (e.key === "s" || e.key === "S") {
      // Bare S only — Ctrl/Cmd+S is the browser's Save, and Alt/Meta combos
      // belong to the OS.
      if (e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
      e.preventDefault();
      toggleBulkMode();
    }
  };

  /**
   * Handles releasing the shift key.
   * @param e the key event
   */
  const handleKeyUp = (e: KeyboardEvent) => {
    if (e.key === "Shift") {
      setShiftKeyDown(false);
    }
  };

  /**
   * Add event listener for keyboard navigation.
   */
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
    // Re-subscribe when any state the handler reads changes, so arrow stepping
    // never captures stale pagination state (currentPage/totalPages/pageSize/
    // the current page's images) while the full-screen view stays open. The
    // bulk-mode and modal flags are here for the same reason: without them `S`
    // and Escape would act on whatever those were when the listener was last
    // bound (AKL-753).
  }, [
    selectedIndex,
    currentPage,
    totalPages,
    paginatedImages,
    pageSize,
    bulkMode,
    bulkPending,
    bulkEditorOpen,
    pointToSpeciesOpen,
    showFilterPopup,
  ]);

  /**
   * Handles selecting an image. If the shift key is pressed, selected multiple images. Otherwise, open the image in full screen.
   * @param index the index of the selected image (relative to current page)
   */
  /** Drop the whole selection. */
  const clearSelection = () => setSelectedImageIds(new Set());

  // Drop selected ids whose photo is no longer on the page. `refreshCurrentView`
  // deliberately does NOT clear the selection — it re-queries in place after a
  // review edit — so a photo can leave the current page under a live selection
  // (e.g. filtering on unreviewed while photos become reviewed). Without this
  // the bar would count photos that are no longer there, the confirmations would
  // promise the wrong number, and an entirely stale selection would post an
  // empty `media_ids`. Same pruning the AI Mappings table does after a refetch.
  useEffect(() => {
    setSelectedImageIds((prev) => {
      if (prev.size === 0) return prev;
      const onPage = new Set(paginatedImages.map((image) => image.id));
      const next = new Set(Array.from(prev).filter((id) => onPage.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [paginatedImages]);

  /**
   * Toggle one photo in or out of the selection.
   * @param imageId the photo's `Image.id`
   */
  const toggleImageSelected = (imageId: string) => {
    setSelectedImageIds((prev) => {
      const next = new Set(prev);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });
  };

  // The selection as media asset ids, in grid order. Derived from the current
  // page rather than stored: the selection clears on any page or filter change,
  // so everything selected is on screen by construction.
  const selectedMediaIds = paginatedImages
    .filter((image) => selectedImageIds.has(image.id))
    .map((image) => Number(image.id));

  /**
   * The "Also set photos to Reviewed" opt-in, shared by the accept and
   * add-observation confirmations so the two cannot drift on its label or
   * behaviour. Rendered through `ConfirmationModal`'s `extraContent` slot.
   * @returns the checkbox row
   */
  const renderSetReviewedCheckbox = () => (
    <label className="flex items-center gap-2 text-sm text-off-black-light dark:text-primary-off-white">
      <input
        type="checkbox"
        className="h-4 w-4 accent-sky-blue-dark"
        checked={bulkSetReviewed}
        onChange={(e) => setBulkSetReviewed(e.target.checked)}
        data-testid="bulk-set-reviewed"
      />
      {t("Also set photos to Reviewed")}
    </label>
  );

  /**
   * Mark the given photos reviewed in the grid's own state, so the badge and the
   * dimmed thumbnail flip immediately.
   *
   * Called only after a successful response, and only for the media the response
   * says were applied — never optimistically, and never for a skipped photo.
   * `refreshCurrentView` afterwards is what brings the bounding-box colours,
   * which are composed from the observations the server now holds and cannot be
   * recomputed from a summary response.
   * @param mediaIds the media the action actually applied to
   */
  const markImagesReviewedLocally = (mediaIds: number[]) => {
    if (mediaIds.length === 0) return;
    const applied = new Set(mediaIds.map(String));
    setPaginatedImages((prev) =>
      prev.map((image) =>
        applied.has(image.id)
          ? { ...image, review_status: "reviewed" as const }
          : image,
      ),
    );
  };

  /**
   * Close whichever bulk flow is open. Both are cleared rather than only the one
   * that started the commit: a finished or failed action must not leave a modal
   * on screen over a cleared selection.
   */
  const closeBulkFlows = () => {
    setBulkPending(null);
    setPointToSpeciesOpen(false);
  };

  /**
   * Run one bulk action, report what it did, then close whichever flow was open,
   * clear the selection and refresh the page so reviewed badges and observation
   * counts update in place.
   *
   * One request for the whole selection — never a loop over the selected photos.
   * @param action the bulk API call to run
   * @param setsReviewed whether this action was asked to mark photos Reviewed —
   *   drives the immediate local badge flip, which the response's `skipped` list
   *   narrows to the photos it actually applied to
   * @param skipMessage the reason-specific skip sentence this action can name,
   *   passed straight to `describeBulkResult` (AKL-811); omitted by the three
   *   AKL-753 actions, which report skips generically
   */
  const runBulkAction = async (
    action: () => Promise<BulkReviewActionResponse>,
    setsReviewed: boolean,
    skipMessage?: SkipReasonMessage,
  ) => {
    if (bulkBusy) return;
    // Belt-and-braces alongside the pruning effect above: never post an empty
    // selection. The API rejects it, and the reviewer would see a generic
    // failure for something that was really "there is nothing selected".
    if (selectedMediaIds.length === 0) {
      closeBulkFlows();
      clearSelection();
      return;
    }
    setBulkBusy(true);
    try {
      const summary = await action();
      const report = describeBulkResult(summary, skipMessage);
      toast[report.tone](t(report.key, report.vars));
      closeBulkFlows();
      // Flip the badges before clearing the selection, from the response's own
      // account of what landed: everything asked for, minus what it skipped. A
      // replay wrote nothing this time, so it flips nothing.
      if (setsReviewed && !summary.replayed) {
        const skipped = new Set(summary.skipped.map((s) => s.media_id));
        markImagesReviewedLocally(
          selectedMediaIds.filter((id) => !skipped.has(id)),
        );
      }
      clearSelection();
      await refreshCurrentView();
    } catch (err) {
      // The interceptor rejects 4xx without toasting, so a failure here must
      // say something — otherwise the confirmation just closes and the reviewer
      // cannot tell the action from a dead button. `bulkErrorMessageKey`
      // returns null only for the cases the interceptor has already reported.
      const key = bulkErrorMessageKey(err);
      if (key) toast.error(t(key));
      closeBulkFlows();
    } finally {
      setBulkBusy(false);
    }
  };

  const handleSelectImage = (index: number) => {
    // In bulk-select mode a plain click selects rather than opens — that is the
    // whole point of the mode, and it is why opening a photo has to be done from
    // outside it. Shift-click still selects when the mode is off, so the older
    // habit keeps working.
    if (bulkMode || shiftKeyDown) {
      toggleImageSelected(paginatedImages[index].id);
      return;
    }
    setSelectedIndex(index);
    // Clear selected images
    clearSelection();
  };

  /**
   * Leave bulk-select mode, dropping the selection and parking focus on the
   * Bulk Select button.
   *
   * Leaving always drops the selection — otherwise the checkboxes disappear
   * while the selection quietly survives, which is the invisible-selection
   * problem the mode exists to remove.
   *
   * The focus move matters for keyboard users: the tile checkbox they were on
   * is about to unmount, and without somewhere to send focus the browser drops
   * it to `<body>` and the next Tab restarts from the top of the page. The
   * button always renders, so it is still in the DOM when this runs.
   */
  const exitBulkMode = () => {
    setBulkMode(false);
    clearSelection();
    bulkToggleRef.current?.focus();
  };

  /** Turn bulk-select mode on, or off via {@link exitBulkMode}. */
  const toggleBulkMode = () => {
    if (bulkMode) exitBulkMode();
    else setBulkMode(true);
  };

  /**
   * Re-queries the current page from the server (bypassing the page cache) so
   * the summary counts (ReviewQueueStats) and the visible grid reflect review
   * edits made in the full-screen viewer — otherwise `counts` keeps the values
   * from the last applyFilters and never moves as the reviewer saves photos.
   * Preserves the reviewer's page position; if reviews shrank the result set so
   * the current page is now out of range, clamps to the last page. The whole
   * page cache is dropped because review edits can shift assets across pages.
   * Updates the grid in place (via its own `refreshing` flag, not `loading`)
   * so closing the viewer doesn't blank the whole grid while this runs in the
   * background. Uses a dedicated flag rather than `loadingMore` because this
   * can overlap with the page-size grow fetch, and sharing one boolean would
   * let whichever resolves first clear it while the other is still running.
   */
  const refreshCurrentView = async () => {
    if (!selectedProject) return;
    if (!isWholeProject && filteredDeployments.length === 0) return;
    if (!validateFilters(filterStateRef.current).valid) return;
    // One snapshot for both page fetches and for the review status committed
    // with the response — see applyFilters for why the live ref is not read
    // after the await.
    const requestedFilters = filterStateRef.current;
    const fetchPage = (page: number) =>
      queryMediaAssets(
        buildMediaQuery(
          selectedProject.project_id,
          {
            ...requestedFilters,
            deploymentIds: isWholeProject
              ? []
              : filteredDeployments.map((d) => d.deployment_id),
          },
          sortStateRef.current,
          (page - 1) * pageSize,
          pageSize,
        ),
      );
    try {
      // Uses refreshing, not loading — the grid only renders when !loading,
      // and this refresh should update it in place (once the new data
      // arrives) rather than blanking the whole grid on every viewer close.
      setRefreshing(true);
      let page = currentPage;
      let result = await fetchPage(page);
      const newTotalPages = Math.ceil(result.total / pageSize);
      if (page > newTotalPages && newTotalPages > 0) {
        page = newTotalPages;
        result = await fetchPage(page);
      }
      setTotalImageCount(result.total);
      setTotalPages(Math.ceil(result.total / pageSize));
      setCounts(result.counts);
      setCountsReviewStatus(requestedFilters.reviewStatus);
      setCurrentPage(page);
      const images = getImagesFromMediaAssetsDto(
        result,
        filteredDeployments,
        selectedProject.guid,
      );
      setPaginatedImages(images);
      setImageCache(new Map([[page, images]]));
    } catch (error) {
      console.error("Error refreshing review view:", error);
    } finally {
      setRefreshing(false);
    }
  };

  /**
   * Handles closing the full screen image view and removing the image name from the URL.
   */
  const handleCloseImage = () => {
    setSelectedIndex(null);
    if (selectedProject && singleSelectedDeployment) {
      navigate(
        `/review/${selectedProject.guid}/${singleSelectedDeployment.guid}`,
        {
          replace: true,
        },
      );
    } else if (selectedProject) {
      navigate(`/review/${selectedProject.guid}`, { replace: true });
    } else {
      navigate(`/review`, { replace: true });
    }
    // Refresh counts + grid so the reviewer's edits in the viewer are reflected
    // (fire-and-forget; the function manages its own loading state).
    void refreshCurrentView();
  };

  // Filter/sort context passed to the detail endpoint so the server can compute
  // the progress position over the same filtered ordering the feed paginates by.
  // Mirrors exactly how `applyFilters` builds the feed query (incl. deployment
  // scope) so server navigation agrees with the on-screen stepping.
  const navigationContext = useMemo<MediaDetailNavigationContext | null>(() => {
    if (!selectedProject) return null;
    const query = buildMediaQuery(
      selectedProject.project_id,
      {
        ...filterState,
        deploymentIds: isWholeProject
          ? []
          : filteredDeployments.map((d) => d.deployment_id),
      },
      sortState,
      0,
      pageSize,
    );
    return { filters: query.filters, sort: query.sort };
  }, [
    selectedProject,
    filterState,
    sortState,
    isWholeProject,
    filteredDeployments,
    pageSize,
  ]);

  // Same chip list ReviewQueueHeader renders — shared so the "Filters"
  // button's count badge can never disagree with the chip row.
  const filterChipCount = useMemo(
    () => buildFilterChips(filterState, selectedDeployments, t).length,
    [filterState, selectedDeployments, t],
  );

  const canStepPrev =
    selectedIndex !== null && (selectedIndex > 0 || currentPage > 1);
  const canStepNext =
    selectedIndex !== null &&
    (selectedIndex < paginatedImages.length - 1 || currentPage < totalPages);

  /**
   * Renders the Manage Review page.
   * @returns the Manage Review page
   */
  return (
    <div className="w-full">
      <div className="mt-8 pl-8 pr-4 pb-16">
        <div className={tailwindClassnames.pageHeaderRow}>
          <div>
            <h1 className={tailwindClassnames.pageTitle}>
              {selectedProject
                ? t("Review Images") + " - " + selectedProject.project_name
                : t("Review Images")}
            </h1>
            <p className={tailwindClassnames.pageSubtitle}>
              {t("Browse and label captured media")}
            </p>
          </div>
          <div className="flex flex-row items-center gap-2">
            {selectedProject && (
              <button
                className="dark:bg-storm-blue bg-off-white-dark p-2 rounded focus-visible:ring-2 focus-visible:ring-sky-blue"
                onClick={() => setShowExportPanel(true)}
                aria-label={t("Export data")}
                title={t("Export data")}
              >
                <DownloadIcon />
              </button>
            )}
          </div>
        </div>

        <ExportPanel
          isOpen={showExportPanel}
          onClose={() => setShowExportPanel(false)}
          projectId={selectedProject?.project_id ?? null}
          projectName={selectedProject?.project_name}
          // Built from the same helper the feed uses, so the export scope is
          // exactly the working set on screen.
          filters={
            selectedProject
              ? buildMediaQuery(
                  selectedProject.project_id,
                  scopedFilterState,
                  sortState,
                  0,
                  null,
                ).filters
              : {}
          }
          matchingCount={totalImageCount}
        />

        <div className={showFilterPopup ? "" : "hidden"}>
          <ReviewPageFilter
            ref={reviewFilterRef}
            isOpen={showFilterPopup}
            setLoadingPage={setLoading}
            loadingProjects={loadingProjects}
            projects={projects}
            selectedProject={selectedProject}
            setSelectedProject={setSelectedProject}
            selectedDeployments={selectedDeployments}
            setSelectedDeployments={setSelectedDeployments}
            applyFilters={applyFilters}
            onClose={() => setShowFilterPopup(false)}
            filterState={filterState}
            updateFilterState={updateFilterState}
            counts={counts}
          />
        </div>
        {selectedIndex !== null &&
          paginatedImages[selectedIndex] &&
          selectedProject &&
          !loading && (
            <FullScreenImageReview
              image={paginatedImages[selectedIndex]}
              mediaId={Number(paginatedImages[selectedIndex].id)}
              projectId={selectedProject.project_id}
              navigationContext={navigationContext}
              images={paginatedImages}
              currentIndex={selectedIndex}
              position={(currentPage - 1) * pageSize + selectedIndex + 1}
              total={totalImageCount}
              canStepPrev={canStepPrev}
              canStepNext={canStepNext}
              onStep={stepImage}
              onSelectIndex={openImageAt}
              onClose={() => handleCloseImage()}
            />
          )}

        {/* Both branches render in this one spot, right below the stats
            cards and above the density row — so the "Filters" button never
            relocates across the page, whether or not a filter has been
            applied yet. */}
        {filtersApplied && selectedProject ? (
          <>
            <ReviewQueueStats
              counts={counts}
              reviewStatus={countsReviewStatus}
            />
            <ReviewQueueHeader
              filterState={filterState}
              sortState={sortState}
              gridDensity={gridDensity}
              selectedDeployments={selectedDeployments}
              onOpenBuilder={() => setShowFilterPopup(true)}
              onClearFilter={handleClearFilter}
              onSortChange={handleSortChange}
              onToggleCaptureDirection={handleToggleCaptureDirection}
              onReshuffle={handleReshuffle}
              onDensityChange={setGridDensity}
              // Rendered inside the header's density row so the two controls
              // share one line rather than stacking.
              bulkActions={
                <BulkActionsBar
                  bulkMode={bulkMode}
                  onToggleMode={toggleBulkMode}
                  toggleRef={bulkToggleRef}
                  // The count the actions will actually act on, not the raw
                  // selection. The two differ for one render after a selected
                  // photo leaves the page, and the bar should never advertise
                  // more than the confirmations are about to commit.
                  selectedCount={selectedMediaIds.length}
                  onMarkBlank={() => setBulkPending({ kind: "mark-blank" })}
                  onAcceptPredictions={() => {
                    setBulkSetReviewed(true);
                    setBulkPending({ kind: "accept" });
                  }}
                  onAddObservation={() => {
                    // Reset the opt-in to its default every time the flow is
                    // opened, so a previous batch's answer never carries into
                    // the next.
                    setBulkSetReviewed(true);
                    setBulkEditorOpen(true);
                  }}
                  // No `setBulkSetReviewed(true)` reset here: the modal owns its
                  // own reviewed opt-in and mounts fresh each time it opens.
                  onPointToSpecies={() => setPointToSpeciesOpen(true)}
                  onClear={clearSelection}
                  busy={bulkBusy}
                />
              }
            />
          </>
        ) : (
          <div className="mb-4">
            <button
              type="button"
              onClick={() => setShowFilterPopup(true)}
              className="flex items-center gap-1 dark:bg-storm-blue bg-off-white-dark p-2 rounded text-sm text-off-black-dark dark:text-primary-off-white transition duration-300 focus-visible:ring-2 focus-visible:ring-sky-blue"
              aria-label={t("Edit filters")}
            >
              <FilterIcon />
              <span>{t("Filters")}</span>
              {filterChipCount > 0 && (
                <span className="ml-1 px-1.5 rounded-full bg-sky-blue text-storm-blue text-xs font-bold">
                  {filterChipCount}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Below the bulk-select bar, directly above the grid it is loading.
            BeatLoader renders nothing when it isn't loading, so this wrapper is
            an empty element the rest of the time — its margin is kept only while
            it actually has a spinner in it, rather than reserving the space
            permanently. */}
        <div
          className={`sweet-loading flex justify-center w-full ${
            loading ? "mb-4" : ""
          }`}
        >
          <BeatLoader
            color={"#B0E6FA"}
            loading={loading}
            size={15}
            margin={5}
          />
        </div>

        <div
          className="w-full grid gap-4 px-2"
          style={{
            gridTemplateColumns: `repeat(auto-fit, minmax(${DENSITY_MIN_WIDTH[gridDensity]}px, 1fr))`,
            overflow: "hidden",
          }}
        >
          {!loading &&
            paginatedImages.map((image, index) => (
              // AKL-753: the checkbox is a sibling of the tile, not a prop on
              // it — BoundingBoxOverlay is shared with the full-screen surface,
              // and a local wrapper keeps the selection affordance out of it.
              // `isolate` makes this wrapper its own stacking context, exactly
              // as BoundingBoxOverlay does for its own badges: without it the
              // checkbox's z-index competes in the page root and can paint over
              // the confirmation modals and the full-screen surface.
              // Keyed by id, not url: a signed url can change for the same
              // photo, which would remount the tile for no reason.
              <div key={image.id} className="group relative isolate">
                <BoundingBoxOverlay
                  loading={index < 8 ? "eager" : "lazy"}
                  fetchPriority={index < 8 ? "high" : undefined}
                  imageUrl={image.url}
                  imageAlt={t("Open photo {{n}}", { n: index + 1 })}
                  // AKL-731: `image.boxes` is already fully composed (visibility-
                  // aware color, resolved/fallback label, rejection already
                  // suppressed server-side) — the grid no longer hardcodes a
                  // single color for every box regardless of review state.
                  boxes={image.boxes ?? []}
                  frameLabels={image.frameLabels ?? []}
                  usePercent={false}
                  boxStyle={{ borderWidth: 1 }}
                  reviewed={image.review_status === "reviewed"}
                  // Top-right in both modes, so the badge never moves under the
                  // reviewer (Margaux, 2026-08-28). The bulk-select checkbox
                  // takes top-left, which is where the badge used to sit. Note
                  // the frame-label badges also render top-right: on a photo
                  // carrying a whole-image classification the two share that
                  // corner, and the badge (z-[1001]) paints over the label.
                  reviewedBadgePosition="top-right"
                  onClick={() => handleSelectImage(index)}
                  style={{
                    // sky-blue (#B0E6FA), the same accent the actions bar and
                    // the focus rings use — the previous raw CSS `blue` was off
                    // palette. Transparent rather than `none` when unselected so
                    // the border is always in the layout and ticking a photo
                    // cannot nudge the grid.
                    border: `3px solid ${
                      selectedImageIds.has(image.id) ? "#B0E6FA" : "transparent"
                    }`,
                    cursor: "pointer",
                    borderRadius: "8px",
                  }}
                />
                {/* Only in bulk-select mode, and on every tile at once — the
                    mode is what announces that selecting is possible, so there
                    is nothing to discover on hover any more. Top-left; the
                    reviewed badge moves to bottom-right to make room. z-10 puts
                    it above the image and boxes within this wrapper's own
                    stacking context. */}
                {bulkMode && (
                  <input
                    type="checkbox"
                    className="absolute top-2 left-2 z-10 h-5 w-5 cursor-pointer accent-sky-blue-dark focus-visible:ring-2 focus-visible:ring-sky-blue-light"
                    checked={selectedImageIds.has(image.id)}
                    // The tile itself toggles selection in this mode, so a click
                    // that lands on the checkbox must not also reach it — that
                    // would toggle twice and cancel out.
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleImageSelected(image.id)}
                    aria-label={t("Select photo {{n}}", { n: index + 1 })}
                    data-testid="review-tile-checkbox"
                  />
                )}
              </div>
            ))}
        </div>

        <div className="sweet-loading flex justify-center w-full my-2">
          <BeatLoader
            color={"#B0E6FA"}
            loading={loadingMore || refreshing}
            size={10}
            margin={3}
          />
        </div>

        {totalImageCount === 0 && loading === false && (
          <div className="text-left text-gray-500 w-full px-2 mt-4">
            {filtersApplied
              ? t(
                  "No photos available for the selected filters. Use the search filter to find photos to review.",
                )
              : t("Use the search filter to find photos to review.")}
          </div>
        )}

        {totalPages > 0 && (
          <Pagination
            showText={true}
            handlePageClick={async (e: any) =>
              await onPageClick(e.selected + 1)
            }
            pageCount={totalPages}
            resultStart={currentPage * pageSize - pageSize + 1}
            resultEnd={Math.min(currentPage * pageSize, totalImageCount)}
            totalCount={totalImageCount}
            take={pageSize}
            initialPage={currentPage - 1}
            loading={loading}
            mainclassname="fixed w-full bottom-0 left-0 right-0 px-4 pt-3 flex flex-row justify-between bg-primary-off-white/50 dark:bg-storm-blue/50 backdrop-blur-sm transition duration-300"
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={handlePageSizeChange}
          />
        )}

        {/* AKL-753 bulk add-observation, stage 1: the same editor the
            single-photo surface uses — species, count and an optional
            comment — followed by its own confirmation below. */}
        {bulkEditorOpen && selectedProject && (
          <ObservationEditor
            title={t("Add observation")}
            projectId={selectedProject.project_id}
            initial={{
              taxonId: null,
              label: null,
              individualCount: 1,
              comments: null,
            }}
            onConfirm={(fields, label) => {
              setBulkEditorOpen(false);
              setBulkPending({ kind: "add-observation", fields, label });
            }}
            onCancel={() => setBulkEditorOpen(false)}
            busy={bulkBusy}
          />
        )}

        {/* AKL-811 point to one species: single-stage — this modal is the
            confirmation, and it commits the whole selection in one request. */}
        {pointToSpeciesOpen && selectedProject && (
          <PointToSpeciesModal
            projectId={selectedProject.project_id}
            // The count the request will actually carry, and the same value the
            // bar renders — so the modal and the bar can never disagree, and
            // neither can advertise a photo that has left the page.
            selectedCount={selectedMediaIds.length}
            busy={bulkBusy}
            onCancel={() => setPointToSpeciesOpen(false)}
            onConfirm={({ taxonId, comments, setReviewed }) =>
              runBulkAction(
                () =>
                  bulkPointToSpecies({
                    media_ids: selectedMediaIds,
                    taxon_id: taxonId,
                    comments,
                    set_reviewed: setReviewed,
                  }),
                setReviewed,
                pointToSpeciesSkip,
              )
            }
          />
        )}

        {/* The three confirmations. Their wording is approved verbatim — the
            counts are interpolated, never concatenated, and none of it may be
            reworded without sign-off. */}
        <ConfirmationModal
          isOpen={bulkPending?.kind === "mark-blank"}
          confirmationMessage={t(
            "This will reject all AI predictions across {{count}} photos and mark all photos as 'blank' and set their status to 'Reviewed'. Continue?",
            { count: selectedMediaIds.length },
          )}
          confirmLabel={t("Mark as blank")}
          danger
          saving={bulkBusy}
          onCancel={() => setBulkPending(null)}
          // Mark as blank always sets Reviewed — no opt-in, so `true`.
          onConfirm={() =>
            runBulkAction(() => bulkMarkBlank(selectedMediaIds), true)
          }
        />

        <ConfirmationModal
          isOpen={bulkPending?.kind === "accept"}
          // Approved 2026-08-28: no Reviewed clause, whether or not the checkbox
          // is ticked. The checkbox itself says what the status step will do, so
          // stating it in the sentence too would only be a second thing to keep
          // in step with it.
          confirmationMessage={t(
            "This will accept all unreviewed AI predictions across {{count}} photos. Continue?",
            { count: selectedMediaIds.length },
          )}
          confirmLabel={t("Accept AI Predictions")}
          saving={bulkBusy}
          extraContent={renderSetReviewedCheckbox()}
          onCancel={() => setBulkPending(null)}
          onConfirm={() =>
            runBulkAction(
              () => bulkAcceptSuggestions(selectedMediaIds, bulkSetReviewed),
              bulkSetReviewed,
            )
          }
        />

        {bulkPending?.kind === "add-observation" && (
          <ConfirmationModal
            isOpen={true}
            confirmationMessage={t(
              "This will create an Observation of {{individualCount}} {{species}} for each of the {{count}} photos you have selected. Continue?",
              {
                individualCount: bulkPending.fields.individualCount ?? 1,
                species: bulkPending.label ?? "",
                count: selectedMediaIds.length,
              },
            )}
            confirmLabel={t("Add observation")}
            saving={bulkBusy}
            extraContent={
              <div className="space-y-3">
                {/* The comment shown back on the confirmation when one was
                    entered — its own line rather than folded into the approved
                    sentence, so that wording stays exactly as approved. */}
                {bulkPending.fields.comments && (
                  <p className="text-sm text-off-black-light dark:text-primary-off-white/80">
                    {t("Comment: {{comment}}", {
                      comment: bulkPending.fields.comments,
                    })}
                  </p>
                )}
                {renderSetReviewedCheckbox()}
              </div>
            }
            onCancel={() => setBulkPending(null)}
            onConfirm={() =>
              runBulkAction(
                () =>
                  bulkCreateObservation({
                    media_ids: selectedMediaIds,
                    taxon_id: bulkPending.fields.taxonId,
                    individual_count: bulkPending.fields.individualCount,
                    comments: bulkPending.fields.comments,
                    set_reviewed: bulkSetReviewed,
                  }),
                bulkSetReviewed,
              )
            }
          />
        )}
      </div>
    </div>
  );
};

export default ManageReviewPage;
